import React, { useState } from "react";
import { Button, Table } from "reactstrap";
import {
  ArchivoHistorico,
  ArchivoId,
  StatusAutorizacion
} from "../../../expedientsV2controls/types";
import { formatIsoDate, formatEnum } from "../../../../helpers/formatters";
import constants from "../../../../../utils/constants";
import BaseButtonWithIconAndTooltip from "../../../../../toolkit/baseButtonWithIconAndTooltip";
import {
  faCheckCircle,
  faEye,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DeportivaEventoAsignable from "../../../../../types/DeportivaEventoAsignable";
import ModalFrame from "../../../../../toolkit/modalFrame";

interface Props {
  row: DeportivaEventoAsignable;
  isOpen: boolean;
  archivos: ArchivoHistorico[];
  archivo: ArchivoId;
  loading: boolean;
  onClose: () => void;
  onRecuperar: (row: DeportivaEventoAsignable, archivoId: ArchivoId) => void;
}

export const BAsignacionArchivosPopUp = ({
  row,
  isOpen,
  archivos,
  archivo,
  loading,
  onClose,
  onRecuperar
}: Props) => {
  // Documento seleccionado para recuperar
  const [archivoId, setArchivoId] = useState<ArchivoId>(null);

  const isArchivoActual = (doc: ArchivoHistorico) => {
    return (
      archivo &&
      doc.idExpediente === archivo.idExpediente &&
      doc.idItem === archivo.idItem
    );
  };

  const onVer = (doc: ArchivoHistorico) => {
    if (!doc.storage) {
      return;
    }
    const url = `${constants.BASE_URL}/${constants.DOCUMENTS_BASE_URL}/${doc.storage}`;
    window.open(url, "_blank");
  };

  // Renderiza el estado del documento
  const renderEstado = (status: StatusAutorizacion) => {
    switch (status) {
      case StatusAutorizacion.AUTORIZADO:
        return (
          <h6>
            <span className="badge badge-success">Firmado</span>
          </h6>
        );
      case StatusAutorizacion.NO_AUTORIZADO:
        return (
          <h6>
            <span className="badge badge-warning">Pendiente</span>
          </h6>
        );
      case StatusAutorizacion.NO_GENERADO:
        return (
          <h6>
            <span className="badge badge-secondary">No Generado</span>
          </h6>
        );
      default:
        return (
          <h6>
            <span className="badge badge-secondary">{formatEnum(status)}</span>
          </h6>
        );
    }
  };

  // Renderiza el modal
  const renderModal = content => {
    return (
      <ModalFrame
        title={`Recuperar documento firmado`}
        isOpen={isOpen}
        toggle={onClose}
        size="lg"
      >
        <p className="text-muted mb-3">
          Seleccione un documento firmado anteriormente para recuperarlo.
        </p>

        {loading ? (
          <div className="text-center py-4">
            <FontAwesomeIcon icon={faSpinner} spin color="#777" />
            &nbsp; Comprobando firmas
          </div>
        ) : archivos.length === 0 ? (
          <div className="alert alert-info">
            No hay archivos disponibles para este producto
          </div>
        ) : (
          content
        )}

        <div className="text-right">
          <Button onClick={onClose}>Cancelar</Button>
          &nbsp;
          <Button
            onClick={() => {
              if (archivoId) {
                onRecuperar(row, archivoId);
              }
            }}
            disabled={!archivoId || loading}
          >
            Recuperar Documento
          </Button>
        </div>
      </ModalFrame>
    );
  };

  return renderModal(
    <div className="table-responsive">
      <Table size="sm">
        <thead>
          <tr>
            <th style={{ width: "40px" }}></th>
            <th>Estado</th>
            <th>Creado Por</th>
            <th>Fecha de Creación</th>
            <th className="text-center">Ver</th>
            <th className="text-center">Actual</th>
          </tr>
        </thead>
        <tbody>
          {archivos.map((doc, index) => {
            const isActual = isArchivoActual(doc);
            const isFirmado =
              doc.statusAutorizacion === StatusAutorizacion.AUTORIZADO;
            const isSelected =
              archivoId &&
              archivoId.idExpediente === doc.idExpediente &&
              archivoId.idItem === doc.idItem;

            return (
              <tr
                key={index}
                className={`${isActual ? "table-primary" : ""} ${
                  isSelected ? "table-active" : ""
                }`}
              >
                <td className="text-center">
                  {isFirmado && !isActual && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      style={{
                        cursor: "pointer"
                      }}
                      onChange={() => {
                        setArchivoId({
                          idExpediente: doc.idExpediente,
                          idItem: doc.idItem
                        });
                      }}
                    />
                  )}
                </td>
                <td>{renderEstado(doc.statusAutorizacion)}</td>
                <td>
                  {doc.responsableNombre}
                  <br />
                  <small className="text-muted">
                    No. Empleado: {doc.responsableNumeroEmpleado}
                  </small>
                </td>
                <td>{formatIsoDate(doc.fechaCreacion)}</td>
                <td className="text-center">
                  {doc.storage && (
                    <BaseButtonWithIconAndTooltip
                      id={`ver-doc-${doc.idExpediente}-${doc.idItem}`}
                      icon={faEye}
                      tooltipTitle="Visualizar documento"
                      onClick={() => {
                        onVer(doc);
                      }}
                      color="#0073e6"
                      size="sm"
                    />
                  )}
                </td>
                <td className="text-center">
                  {isActual && (
                    <FontAwesomeIcon icon={faCheckCircle} color={"#060"} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
